<template>
    <VContainer class="sr-filter ma-0 pa-0">
        <div class="d-flex justify-space-between mb-3">
            <h4 class="brand-blue--text">
                {{ title }}
            </h4>

            <SrFilterButtons
                :clearable="clearable"
                :applicable="applicable"
                :saveable="saveable"
                :is-saveable="isSaveable"
                :has-changes="hasChanges"
                :has-value="hasValue"
                :is-applied="isApplied"
                :apply-button-text="applyButtonText"
                :save-button-text="saveButtonText"
                :clear-button-text="clearButtonText"
                @save="save"
                @apply="apply"
                @clear="clear"
            />
        </div>

        <VRow>
            <SrFilterItem
                v-for="filterConfig in config"
                :key="filterConfig.id"
                v-model="getFilterById(filterConfig.id).value"
                :config="filterConfig"
            />
        </VRow>
    </VContainer>
</template>

<script>
import SrFilterButtons from './SrFilterButtons';
import SrFilterItem from './SrFilterItem';
import Filter from './Filter';
import Predicate from './Predicate';

export default {
    name: 'SrFilter',
    components: {
        SrFilterButtons,
        SrFilterItem,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        title: {
            type: String,
            default: 'Filter',
        },
        config: {
            type: Array,
            required: true,
            validator(config) {
                let isValidationResult = true;
                const mandatoryProperties = ['id', 'type', 'label'];
                const typeBasedMandatoryProperties = {
                    text: ['hint', 'placeholder'],
                    select: ['items'],
                    date: [],
                };

                config.forEach((configItem) => {
                    const properties = mandatoryProperties.concat(typeBasedMandatoryProperties[configItem.type]);
                    properties.forEach((key) => {
                        if (!Object.prototype.hasOwnProperty.call(configItem, key)) {
                            isValidationResult = false;
                        }
                    });
                });

                return isValidationResult;
            },
        },
        applicable: {
            type: Boolean,
            default: false,
        },
        applyButtonText: {
            type: String,
            default: 'Apply',
        },
        saveable: {
            type: Boolean,
            default: false,
        },
        saveButtonText: {
            type: String,
            default: 'Save Filter',
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        clearButtonText: {
            type: String,
            default: 'Clear',
        },
    },

    data() {
        return {
            filtersById: {},
            originalFiltersById: {},
            isApplied: false,
        };
    },

    computed: {
        hasValue() {
            return this.filtersWithValues.length > 0;
        },
        hasChanges() {
            return JSON.stringify(this.filtersWithValues) !== JSON.stringify(this.originalFiltersWithValues);
        },
        isSaveable() {
            return this.originalFiltersWithValues.length > 0 && !this.hasChanges;
        },
        filtersAsArray() {
            return this.config.map((filterConfig) => this.filtersById[filterConfig.id]);
        },
        filtersWithValues() {
            return this.getPredicatesWithValues(this.filtersAsArray);
        },
        originalFiltersAsArray() {
            return this.config.map((filterConfig) => this.originalFiltersById[filterConfig.id]);
        },
        originalFiltersWithValues() {
            return this.getPredicatesWithValues(this.originalFiltersAsArray);
        },
    },
    watch: {
        value: {
            handler(filter) {
                if (JSON.stringify(this.getPredicatesWithValues(filter.children)) === JSON.stringify(this.filtersWithValues)) {
                    return;
                }

                this.initFiltersFromValueProp();
                this.apply();
            },
            deep: true,
        },
        filtersById: {
            handler() {
                if (!this.applicable) {
                    this.emitInputEvent(this.filtersAsArray);
                }
            },
            deep: true,
        },
    },
    created() {
        this.initEmptyFilters();
    },
    methods: {
        initEmptyFilters() {
            const filtersById = {};
            this.config.forEach((filterConfig) => {
                filtersById[filterConfig.id] = new Predicate(filterConfig.id);
            });
            this.filtersById = filtersById;
        },
        initFiltersFromValueProp() {
            const filtersById = {};
            this.value.children.forEach((filter) => {
                filtersById[filter.filterCriteriaId] = new Predicate(
                    filter.filterCriteriaId,
                    filter.negated,
                    filter.predicateDefinitionId,
                    JSON.parse(JSON.stringify(filter.parameterValue)),
                );
            });
            this.filtersById = filtersById;
        },
        apply() {
            this.originalFiltersById = JSON.parse(JSON.stringify(this.filtersById));
            this.isApplied = true;
            this.emitInputEvent(this.filtersAsArray);
        },
        emitInputEvent(filters) {
            this.$emit('input', new Filter(this.getPredicatesWithValues(JSON.parse(JSON.stringify(filters)))));
        },
        save() {
            if (this.saveable) {
                this.$emit('save');
            }
        },
        clear() {
            this.filtersById = [];
            this.apply();
        },
        getFilterById(id) {
            if (!this.filtersById[id]) {
                this.$set(this.filtersById, id, new Predicate(id));
            }

            return this.filtersById[id];
        },
        getPredicatesWithValues(predicates) {
            return predicates.filter((predicate) => {
                if (!predicate) {
                    return false;
                }

                const { value } = predicate.parameterValue;
                if (Array.isArray(value)) {
                    return value.length !== 0;
                }
                return !!value;
            });
        },
    },
};
</script>
